
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Printer, BadgeDollarSign, Download } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import RevenueDownloadDialog from './RevenueDownloadDialog';
import RevenueFormatDialog from './RevenueFormatDialog';

interface RevenueHeaderProps {
  onPrintStatement: (format: 'jpg' | 'pdf') => Promise<void>;
  onPrintBreakdown: (format: 'jpg' | 'pdf') => Promise<void>;
  onPrintTeacherSalaries: (format: 'jpg' | 'pdf') => Promise<void>;
  filteredPayments?: any[];
}

export const RevenueHeader = ({
  onPrintStatement,
  onPrintBreakdown,
  onPrintTeacherSalaries,
  filteredPayments = []
}: RevenueHeaderProps) => {
  const navigate = useNavigate();
  const [isDownloading, setIsDownloading] = useState(false);
  const [isStatementDownloading, setIsStatementDownloading] = useState(false);
  const [isBreakdownDownloading, setIsBreakdownDownloading] = useState(false);
  const [isSalariesDownloading, setIsSalariesDownloading] = useState(false);

  // Format dialog states (for statement only - no pagination needed)
  const [isStatementFormatDialogOpen, setIsStatementFormatDialogOpen] = useState(false);

  // Pagination dialog states (for breakdown, salaries, and list)
  const [isBreakdownDialogOpen, setIsBreakdownDialogOpen] = useState(false);
  const [isSalariesDialogOpen, setIsSalariesDialogOpen] = useState(false);
  const [isListDialogOpen, setIsListDialogOpen] = useState(false);

  const handleStatementDownload = async (format: 'jpg' | 'pdf') => {
    try {
      setIsStatementDownloading(true);
      console.log('Statement download with format:', format);
      await onPrintStatement(format);
    } catch (error) {
      console.error('Error downloading statement:', error);
      toast.error('Failed to download statement');
    } finally {
      setIsStatementDownloading(false);
    }
  };

  const handleBreakdownDownload = async (format: 'jpg' | 'pdf', startPage?: number, endPage?: number) => {
    try {
      setIsBreakdownDownloading(true);
      console.log('Breakdown download with format:', format, 'pages:', startPage, endPage);
      await onPrintBreakdown(format);
    } catch (error) {
      console.error('Error downloading breakdown:', error);
      toast.error('Failed to download breakdown');
    } finally {
      setIsBreakdownDownloading(false);
    }
  };

  const handleSalariesDownload = async (format: 'jpg' | 'pdf', startPage?: number, endPage?: number) => {
    try {
      setIsSalariesDownloading(true);
      console.log('Salaries download with format:', format, 'pages:', startPage, endPage);
      await onPrintTeacherSalaries(format);
    } catch (error) {
      console.error('Error downloading salaries:', error);
      toast.error('Failed to download salaries');
    } finally {
      setIsSalariesDownloading(false);
    }
  };

  const handleListDownload = async (format: 'jpg' | 'pdf', startPage?: number, endPage?: number) => {
    if (!filteredPayments || filteredPayments.length === 0) {
      toast.error('No payments to download');
      return;
    }

    try {
      setIsDownloading(true);
      const { printRevenueListPaginatedAsJPG } = await import('@/utils/printing/revenueListPaginatedPrinter');
      
      const paymentsList = filteredPayments.map((payment, index) => ({
        id: (index + 1).toString(),
        date: payment.date || new Date().toISOString(),
        type: payment.type || 'fee',
        amount: payment.amount || 0,
        recipient: payment.recipient || 'N/A',
        status: payment.status || 'pending',
        reference: payment.reference || 'N/A',
        description: payment.description || 'N/A'
      }));
      
      const paymentsPerPage = 18; // Updated to 18
      const totalPages = Math.ceil(paymentsList.length / paymentsPerPage);
      
      if (startPage && endPage) {
        await printRevenueListPaginatedAsJPG(paymentsList, startPage, endPage);
      } else {
        await printRevenueListPaginatedAsJPG(paymentsList, 1, totalPages);
      }
    } catch (error) {
      console.error('Error downloading revenue list:', error);
      toast.error('Failed to download revenue list');
    } finally {
      setIsDownloading(false);
    }
  };

  const paymentsPerPage = 18; // Updated to 18
  const totalPayments = filteredPayments?.length || 0;
  const totalPages = Math.ceil(totalPayments / paymentsPerPage);

  // For breakdown and salaries, we'll assume 50 records for pagination demo
  const breakdownItemsPerPage = 18;
  const salariesItemsPerPage = 18;
  const estimatedBreakdownItems = 50; // This would come from actual data
  const estimatedSalaryItems = 30; // This would come from actual data

  return (
    <>
      <div className="flex justify-between items-center">
        <h2 className="text-3xl font-bold tracking-tight">Revenue</h2>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            onClick={() => setIsStatementFormatDialogOpen(true)} 
            disabled={isStatementDownloading}
            className="gap-2"
          >
            <Download className="h-4 w-4" />
            {isStatementDownloading ? 'Downloading...' : 'Download Statement'}
          </Button>
          <Button 
            variant="outline" 
            onClick={() => setIsBreakdownDialogOpen(true)} 
            disabled={isBreakdownDownloading}
            className="gap-2"
          >
            <Download className="h-4 w-4" />
            {isBreakdownDownloading ? 'Downloading...' : 'Download Breakdown'}
          </Button>
          <Button 
            variant="outline" 
            onClick={() => setIsSalariesDialogOpen(true)} 
            disabled={isSalariesDownloading}
            className="gap-2"
          >
            <BadgeDollarSign className="h-4 w-4" />
            {isSalariesDownloading ? 'Downloading...' : 'Download Salaries'}
          </Button>
          <Button 
            variant="outline" 
            onClick={() => setIsListDialogOpen(true)}
            disabled={isDownloading || totalPayments === 0}
            className="gap-2"
          >
            <Download className="h-4 w-4" />
            {isDownloading ? 'Downloading...' : 'Download List'}
          </Button>
          <Button variant="outline" onClick={() => navigate("/")} className="gap-2">
            <ArrowLeft className="h-4 w-4" />
            Back to Dashboard
          </Button>
        </div>
      </div>

      {/* Statement Format Dialog (no pagination) */}
      <RevenueFormatDialog
        isOpen={isStatementFormatDialogOpen}
        onClose={() => setIsStatementFormatDialogOpen(false)}
        onDownload={handleStatementDownload}
        title="Download Revenue Statement"
        description="Choose format for downloading the complete revenue statement"
      />

      {/* Breakdown Dialog with Pagination */}
      <RevenueDownloadDialog
        isOpen={isBreakdownDialogOpen}
        onClose={() => setIsBreakdownDialogOpen(false)}
        onDownload={handleBreakdownDownload}
        title="Download Revenue Breakdown"
        description="Choose page range and format for downloading the detailed revenue breakdown"
        totalItems={estimatedBreakdownItems}
        itemsPerPage={breakdownItemsPerPage}
        showPagination={true}
      />

      {/* Teacher Salaries Dialog with Pagination */}
      <RevenueDownloadDialog
        isOpen={isSalariesDialogOpen}
        onClose={() => setIsSalariesDialogOpen(false)}
        onDownload={handleSalariesDownload}
        title="Download Teacher Salaries"
        description="Choose page range and format for downloading the teacher salary report"
        totalItems={estimatedSalaryItems}
        itemsPerPage={salariesItemsPerPage}
        showPagination={true}
      />

      {/* Revenue List Dialog with Pagination */}
      <RevenueDownloadDialog
        isOpen={isListDialogOpen}
        onClose={() => setIsListDialogOpen(false)}
        onDownload={handleListDownload}
        title="Download Revenue List"
        description="Choose page range and format for downloading the revenue payments list"
        totalItems={totalPayments}
        itemsPerPage={paymentsPerPage}
        showPagination={true}
      />
    </>
  );
};
